import { Link } from "react-router-dom";
import { RANK_THRESHOLDS } from "@/mocks/gameData";

interface Props {
  currentSquare: number;
  turn: number;
  correctCards: number;
  onPlayAgain: () => void;
}

function getRank(square: number) {
  for (const t of RANK_THRESHOLDS) {
    if (square >= t.min) return t;
  }
  return RANK_THRESHOLDS[RANK_THRESHOLDS.length - 1];
}

function getPerformanceLabel(correctCards: number, turn: number) {
  const ratio = turn > 0 ? correctCards / turn : 0;
  if (ratio >= 0.7) return { label: "Elite Responder", color: "#00ff88" };
  if (ratio >= 0.4) return { label: "Solid Engineer", color: "#00d9ff" };
  return { label: "Learning Mode", color: "#f59e0b" };
}

export default function ResultsScreen({ currentSquare, turn, correctCards, onPlayAgain }: Props) {
  const rank = getRank(currentSquare);
  const perf = getPerformanceLabel(correctCards, turn);
  const allRanks = [...RANK_THRESHOLDS].reverse();
  const currentRankIndex = allRanks.findIndex((r) => r.rank === rank.rank);
  const accuracy = turn > 0 ? Math.round((correctCards / Math.max(1, turn)) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-mono flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Grid bg */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,255,136,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.025) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Glow orb behind rank */}
      <div
        className="fixed top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${rank.color}08 0%, transparent 70%)`,
          filter: "blur(40px)",
        }}
      />

      <div className="relative z-10 w-full max-w-3xl">

        {/* Top status bar */}
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-lg mb-8"
          style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88]"></span>
            <span className="text-[10px] text-white/30 font-mono uppercase tracking-widest">Session Complete</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[10px] text-white/20 font-mono">incident_ladder · run_complete</span>
            <span
              className="text-[9px] font-bold font-mono px-2 py-0.5 rounded"
              style={{ background: "rgba(0,255,136,0.1)", color: "#00ff88", border: "1px solid rgba(0,255,136,0.2)" }}
            >
              RESOLVED
            </span>
          </div>
        </div>

        {/* Rank hero */}
        <div className="text-center mb-8">
          <div
            className="inline-flex flex-col items-center p-8 rounded-2xl mb-6"
            style={{
              background: `${rank.color}06`,
              border: `1px solid ${rank.color}25`,
            }}
          >
            <div
              className="w-20 h-20 flex items-center justify-center rounded-full border-2 mb-4"
              style={{
                borderColor: rank.color,
                background: `${rank.color}12`,
                boxShadow: `0 0 30px ${rank.color}25, 0 0 60px ${rank.color}10`,
              }}
            >
              <i className={`${rank.icon} text-3xl`} style={{ color: rank.color }}></i>
            </div>
            <div className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: `${rank.color}80` }}>
              Final Rank Achieved
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2">{rank.rank}</h1>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold font-mono"
              style={{ background: `${perf.color}12`, color: perf.color, border: `1px solid ${perf.color}25` }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: perf.color }}></span>
              {perf.label}
            </div>
          </div>

          <p className="text-white/35 text-sm max-w-md mx-auto leading-relaxed">
            {currentSquare === 100
              ? "You reached square 100. Production is stable. Incident fully resolved. Share your rank."
              : `You reached square ${currentSquare}. Keep practicing — Production Guardian awaits.`}
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { val: currentSquare, label: "Final Square", sub: "/ 100", icon: "ri-map-pin-line", color: "#00d9ff" },
            { val: turn, label: "Total Turns", sub: "rolled", icon: "ri-dice-line", color: "#f59e0b" },
            { val: correctCards, label: "Cards Correct", sub: "answered", icon: "ri-checkbox-circle-line", color: "#00ff88" },
            { val: `${accuracy}%`, label: "Accuracy", sub: "card rate", icon: "ri-bar-chart-line", color: rank.color },
          ].map((s) => (
            <div
              key={s.label}
              className="flex flex-col items-center p-4 rounded-xl"
              style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="w-7 h-7 flex items-center justify-center mb-2">
                <i className={`${s.icon} text-base`} style={{ color: s.color }}></i>
              </div>
              <div className="text-2xl font-black" style={{ color: s.color }}>{s.val}</div>
              <div className="text-[10px] text-white/30 mt-0.5 font-mono">{s.label}</div>
              <div className="text-[9px] text-white/15 font-mono">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Rank progression */}
        <div
          className="rounded-xl p-5 mb-6"
          style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest">Rank Progression</div>
            <div className="text-[10px] text-white/20 font-mono">
              {currentRankIndex + 1} / {allRanks.length} unlocked
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {allRanks.map((r, i) => {
              const isCurrentRank = i === currentRankIndex;
              const isUnlocked = i <= currentRankIndex;
              return (
                <div
                  key={r.rank}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all"
                  style={{
                    background: isCurrentRank ? `${r.color}08` : "transparent",
                    border: isCurrentRank ? `1px solid ${r.color}20` : "1px solid transparent",
                  }}
                >
                  <div
                    className="w-6 h-6 flex items-center justify-center rounded-full border flex-shrink-0"
                    style={{
                      borderColor: isUnlocked ? r.color : "rgba(255,255,255,0.08)",
                      background: isUnlocked ? `${r.color}12` : "transparent",
                    }}
                  >
                    {isUnlocked ? (
                      <i
                        className={`${r.icon} text-[10px]`}
                        style={{ color: r.color }}
                      ></i>
                    ) : (
                      <i className="ri-lock-line text-[10px] text-white/15"></i>
                    )}
                  </div>
                  <span
                    className="text-xs font-bold flex-1"
                    style={{
                      color: isCurrentRank ? "white" : isUnlocked ? r.color : "rgba(255,255,255,0.15)",
                    }}
                  >
                    {r.rank}
                  </span>
                  <span className="text-[10px] text-white/15 font-mono">sq {r.min}+</span>
                  {isCurrentRank && (
                    <span
                      className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded"
                      style={{ background: `${r.color}15`, color: r.color }}
                    >
                      YOU
                    </span>
                  )}
                  {!isCurrentRank && isUnlocked && (
                    <i className="ri-check-line text-[10px]" style={{ color: r.color }}></i>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Share prompt */}
        <div
          className="rounded-xl p-4 mb-5 text-center"
          style={{ background: "rgba(0,255,136,0.04)", border: "1px solid rgba(0,255,136,0.12)" }}
        >
          <p className="text-[11px] text-white/40 font-mono mb-1">
            Share your rank with your team
          </p>
          <p className="text-xs text-white/60 font-mono">
            "I just earned <span style={{ color: rank.color }} className="font-bold">{rank.rank}</span> on Incident Ladder — the SRE board game. Can you beat me?"
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onPlayAgain}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ background: "#00ff88", color: "#0a0a0a" }}
          >
            <i className="ri-refresh-line"></i>
            Play Again
          </button>
          <a
            href="#"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ border: "1px solid rgba(0,217,255,0.3)", color: "#00d9ff" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(0,217,255,0.06)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            <i className="ri-community-line"></i>
            Join Skool Community
          </a>
          <Link
            to="/#how-it-works"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap"
            style={{ border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            <i className="ri-bug-line"></i>
            Failure Patterns
          </Link>
        </div>
      </div>
    </div>
  );
}
